// frontend/src/pages/Portal/SpecialistProfile/state/SpecialistProfileTypes.ts

export type AgeGroupOption = "Adult" | "Pediatric" | "Geriatric" | "Mixed";

export interface BasicProfileState {
  userId: string; // temporary, will later link to real User table
  cpsNumber: string; // CPSO number
  fullName: string;
  gender: string;
  clinicId: string;
  acceptingNewReferrals: string; // "yes" | "no"
  availabilityStatus: string;
}

export interface ClinicalProfileState {
  specialtyId: string;
  // UI uses multi-select; we derive a single AgeGroup string for DB later
  ageGroups: string[];
}

export interface AccessState {
  languageIds: string[]; // from Language table later
  visitTypeIds: string[]; // from VisitType table later
  coverageTypeIds: string[]; // from CoverageType table later
}

export interface HospitalsAndProceduresState {
  hospitalIds: string[];
  hasNoHospitalPrivileges: boolean;
  procedureIds: string[];
  excludedProcedureIds: string[];
}

export interface ConditionsState {
  // Primary conditions this specialist actively accepts
  primaryConditionIds: string[];
  // Conditions they explicitly do NOT manage
  excludedConditionIds: string[];
}

export interface ReferralState {
  requiredInvestigationIds: string[];

  /**
   * These are stored as strings for consistent validation + payload building.
   * Empty string = unknown / not provided.
   */
  routineWaitWeeks: string;
  urgentWaitWeeks: string;

  /**
   * New: referral method codes (e.g., FAX, EREFERRAL, PORTAL...)
   * Stored here so wizard can include them in payload.
   */
  referralMethodCodes: string[];

  otherNotes: string;
}

/**
 * Top-level form state for the Specialist Profile wizard.
 */
export interface SpecialistProfileFormState {
  basicProfile: BasicProfileState;
  clinicalProfile: ClinicalProfileState;
  access: AccessState;
  hospitalsAndProcedures: HospitalsAndProceduresState;
  conditions: ConditionsState;
  referral: ReferralState;
}

/**
 * Error types – one per logical section/tab.
 */
export type BasicProfileErrors = {
  userId?: string;
  cpsNumber?: string;
  fullName?: string;
  gender?: string;
  clinicId?: string;
  acceptingNewReferrals?: string;
  availabilityStatus?: string;
};

export type ClinicalProfileErrors = {
  specialtyId?: string;
  ageGroups?: string;
};

export type AccessErrors = {
  languageIds?: string;
  visitTypeIds?: string;
  coverageTypeIds?: string;
};

export type HospitalsProceduresErrors = {
  hospitalIds?: string;
};

export type ReferralErrors = {
  referralMethodCodes?: string;
  routineWaitWeeks?: string;
  urgentWaitWeeks?: string;
};

export interface SpecialistProfileErrorState {
  basicProfile: BasicProfileErrors;
  clinicalProfile: ClinicalProfileErrors;
  access: AccessErrors;
  hospitalsAndProcedures: HospitalsProceduresErrors;
  referral: ReferralErrors;
}

export const defaultSpecialistProfileFormState: SpecialistProfileFormState = {
  basicProfile: {
    userId: "",
    cpsNumber: "",
    fullName: "",
    gender: "",
    clinicId: "",
    acceptingNewReferrals: "",
    availabilityStatus: "",
  },
  clinicalProfile: {
    specialtyId: "",
    ageGroups: [],
  },
  access: {
    languageIds: [],
    visitTypeIds: [],
    coverageTypeIds: [],
  },
  hospitalsAndProcedures: {
    hospitalIds: [],
    hasNoHospitalPrivileges: false,
    procedureIds: [],
    excludedProcedureIds: [],
  },
  conditions: {
    primaryConditionIds: [],
    excludedConditionIds: [],
  },
  referral: {
    requiredInvestigationIds: [],
    routineWaitWeeks: "",
    urgentWaitWeeks: "",
    referralMethodCodes: [],
    otherNotes: "",
  },
};

export const defaultSpecialistProfileErrorState: SpecialistProfileErrorState = {
  basicProfile: {},
  clinicalProfile: {},
  access: {},
  hospitalsAndProcedures: {},
  referral: {},
};
